import type React from "react";

import { logger } from "@jslib/shared/utils/logger";

import { collectDeviceData } from "@/lib/fraud/fraud";

import { type VerificationService, type VerificationStep } from "../../lib/types/types";
import { FormFooterComponent } from "../FormFooter/FormFooterComponent";
import { dashed } from "../../lib/utils/strings";
import { EventName, type PasteEvent, recordGaEvent } from "../../lib/GoogleAnalytics/ga";

interface PersonalInfoWrapperProps {
  step: VerificationStep;
  verificationService: VerificationService;
  className?: string;
  children: React.ReactNode;
}

const onFormPaste: React.ClipboardEventHandler = (e) => {
  try {
    // we want to grab pastes on any input element inside the collect info step
    const target = e.target instanceof HTMLInputElement ? e.target : undefined;

    if (target && (target.id || target.name)) {
      const event: PasteEvent = {
        eventName: EventName.FORM_PASTE,
        params: {
          id: target.id || target.name,
          queryLength: e.clipboardData.getData("text").length,
        },
      };
      recordGaEvent(event);
    }
  } catch (e) {
    logger.error(e);
  }
};

/**
 * A shared wrapper for all personal info collect steps.
 */
export const PersonalInfoWrapper: React.FC<PersonalInfoWrapperProps> = ({
  step,
  verificationService,
  className,
  children,
}) => {
  const dashedStep = dashed(step).replace(/^collect-/, "");
  const classes = [`sid-${dashedStep}-container`, "sid-l-container", "sid-left-align"];

  if (className) {
    classes.push(className);
  }

  const onFocus = () => {
    const { programTheme, verificationResponse } = verificationService;
    collectDeviceData(programTheme, verificationResponse.verificationId);
  };

  return (
    <div
      id={`sid-step-${dashedStep}`}
      className={classes.join(" ")}
      onPaste={onFormPaste}
      onFocusCapture={onFocus}
    >
      <div role="main">{children}</div>
      <FormFooterComponent verificationService={verificationService} />
    </div>
  );
};
